import type ITemplateMarkup from "@interfaces/contentManager/ITemplateMarkup";
import type IDomPosition from "@interfaces/utility/IDomPosition";
import type { IZIndex } from "@interfaces/utility/IModal";
import { scrollIntoView } from "seamless-scroll-polyfill";
import type { CompassPoint } from "./geoUtils";
import { isInt } from "./numberUtils";
import { isNullOrWhiteSpace } from "./stringUtils";
import { delay } from "./timerUtils";

// Threshold to ignore elements with absurdly high z-index values (stuff like hubspot)
const Z_INDEX_ABUSE_THRESHOLD = 2000000000;

/**
 * Returns true on iOS/iPadOS, including iPadOS 13+ in desktop mode (which reports MacIntel but has touch points).
 */
export const isIosDevice = (): boolean =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

/**
 * Strips HTML tags from a string and returns only the text content.
 * @param html The string containing HTML tags.
 * @returns The text content with HTML tags removed.
 */
export const stripHtmlTags = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

/**
 * Finds the closest parent element of a given element that matches a specified selector.
 * @param $elem The starting element to search from.
 * @param parentSelector The selector to match the parent element against.
 * @returns The closest parent element that matches the selector, or null if none is found.
 */
export const getClosestParentElem = (
  $elem: HTMLElement | any,
  parentSelector: string
): null | HTMLElement => {
  for (; $elem && $elem !== document; $elem = $elem.parentNode) {
    if ($elem.matches(parentSelector)) {
      return $elem;
    }
  }
  return null;
};

/**
 * Resolves with the first element matching the selector, waiting for it to appear if it has not yet.
 * Brand template markup is injected after the page component mounts, so anything from it is absent
 * when onMounted runs.
 * @param selector CSS selector to match
 * @param timeoutMs How long to wait before giving up
 * @returns The element, or null if it never appeared
 */
export const waitForElement = <T extends HTMLElement>(
  selector: string,
  timeoutMs: number = 10000
): Promise<T | null> => {
  const existing = document.querySelector<T>(selector);
  if (existing) {
    return Promise.resolve(existing);
  }

  return new Promise<T | null>((resolve) => {
    let timer: ReturnType<typeof setTimeout> | undefined = undefined;
    const observer = new MutationObserver(() => {
      const $el = document.querySelector<T>(selector);
      if ($el) {
        settle($el);
      }
    });

    const settle = ($el: T | null) => {
      observer.disconnect();
      if (timer !== undefined) {
        clearTimeout(timer);
      }
      resolve($el);
    };

    observer.observe(document.documentElement, { childList: true, subtree: true });
    timer = setTimeout(() => settle(document.querySelector<T>(selector)), timeoutMs);
  });
};

/**
 * Scrolls the window to the top with a smooth behavior. An optional padding can be provided to scroll to a specific position from the top.
 * @param pad The padding from the top to scroll to.
 * @returns void
 */
export const scrollTop = (pad: number = 50) => window.scrollTo({ top: pad, behavior: "smooth" });

/**
 * Scrolls the element with the specified ID into view with a smooth behavior.
 * @param id The ID or element instance of the element to scroll into view.
 * @options scroll options
 * @returns void
 */
export const scrollToElement = (id: string | Element, options?: ScrollIntoViewOptions): void => {
  const elem: Element | null = id instanceof Element ? id : document.getElementById(id);

  if (elem) {
    scrollIntoView(elem, options ?? { behavior: "smooth" });
  }
};

/**
 * Checks if the current device supports mouse input by using the CSS media query for pointer fine.
 * @returns true if the device supports mouse input, false otherwise.
 */
export const hasMouseSupport = (): boolean => matchMedia("(pointer:fine)").matches;

/**
 * Checks if a script with the specified source URL has already been loaded in the document.
 * @param src The source URL of the script to check for.
 * @returns true if a script with the specified source URL is found in the document, false otherwise.
 */
export const hasScriptLoaded = (src: string) =>
  document.querySelector(`script[src="${src}"]`) ? true : false;

/**
 * Checks if a link with the specified href URL has already been loaded in the document.
 * @param src The href URL of the link to check for.
 * @returns true if a link with the specified href URL is found in the document, false otherwise.
 */
export const hasLinkLoaded = (src: string) => (document.querySelector(`link[href="${src}"]`) ? true : false);

/**
 * Dynamically loads a script into the document. If a script with the same source URL already exists, it will not be loaded again.
 * @param src The source URL of the script to load.
 * @param charset The character set of the script. Defaults to an empty string.
 * @param async Whether the script should be loaded asynchronously. Defaults to true.
 * @param defer Whether the script should be deferred. Ignored when `async` is true. Defaults to false.
 */
export const loadScript = (
  src: string,
  charset: string = "",
  async: boolean = true,
  defer: boolean = false
) => {
  const scriptElement = document.querySelector(`script[src="${src}"]`);

  //Script has been loaded, do nothing
  if (scriptElement) {
    return;
  }

  const script = document.createElement("script");
  script.src = src;
  script.charset = charset;
  script.async = async;
  script.defer = async ? false : defer;

  script.addEventListener("load", () => {
    console.log(`Loaded ${src}`);
  });
  document.body.appendChild(script);
};

const scriptLoads = new Map<string, Promise<void>>();

/**
 * Loads a script into the document and resolves once it has loaded. Concurrent and repeat calls for the same
 * source share one load; a script tag already added by {@link loadScript} is treated as loaded.
 * @param src The source URL of the script to load.
 * @returns A promise that resolves when the script loads and rejects if it fails to load.
 */
export const loadScriptAsync = (src: string): Promise<void> => {
  const pending = scriptLoads.get(src);
  if (pending) {
    return pending;
  }

  if (document.querySelector(`script[src="${src}"]`)) {
    return Promise.resolve();
  }

  const load = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", () => {
      scriptLoads.delete(src);
      script.remove();
      reject(new Error(`Failed to load script ${src}`));
    });
    document.body.appendChild(script);
  });

  scriptLoads.set(src, load);
  return load;
};

/**
 * Dynamically loads a stylesheet link into the document head. If a link with the same href already exists, it will not be loaded again.
 * @param href The href URL of the stylesheet to load.
 * @param rel The relationship attribute for the link element. Defaults to `"stylesheet"`.
 * @param type The MIME type of the linked resource. Defaults to `"text/css"`.
 */
export const loadStyle = (href: string, rel: string = "stylesheet", type: string = "text/css") => {
  const linkElement = document.querySelector(`link[href="${href}"]`);

  //Style has been loaded, do nothing
  if (linkElement) {
    return;
  }

  const link = document.createElement("link");
  link.href = href;
  link.rel = rel;
  link.type = type;

  link.addEventListener("load", () => {
    console.log(`Loaded ${href}`);
  });
  document.head.appendChild(link);
};

/**
 * Decodes an HTML-encoded string back to its original text content.
 * @param encodedHtml The HTML-encoded string to decode.
 * @returns The decoded text content, or null if parsing fails.
 */
export const decodeHtml = (encodedHtml: string) => {
  const parser = new DOMParser();
  const decodedHtml = parser.parseFromString(encodedHtml, "text/html").body.textContent;
  return decodedHtml;
};

/**
 * HTML-encodes a plain text string by escaping special characters into their HTML entity equivalents.
 * @param decodedHtml The plain text string to encode.
 * @returns The HTML-encoded string.
 */
export const encodeHtml = (decodedHtml: string) => {
  const encoder = document.createElement("div");
  encoder.textContent = decodedHtml;
  return encoder.innerHTML;
};

/**
 * Sets the page's meta description, adding the tag when the brand template does not already carry one.
 * @param description The description to publish, or empty to leave whatever the template set.
 */
export const setMetaDescription = (description: string): void => {
  if ((description ?? "").trim() === "") {
    return;
  }

  let meta = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "description";
    document.head.appendChild(meta);
  }
  meta.content = description;
};

/**
 * Sets the browser tab title. For the S1 standard master template, animates the title with a loading indicator before settling on the final value.
 * A title the server rendered whole, without the `|>` marker, is left alone when no page title is given.
 * @param pageTemplateTypeId The page template type ID used to determine how the title is set.
 * @param pageTitle The page-specific portion of the title to display.
 */
export const setPageTitle = async (pageTemplateTypeId: number, pageTitle: string) => {
  if (document.title.includes("|>")) {
    const baseTitle = document.title.split("|>")[0].trim();
    document.title = `${baseTitle} | =>`;
    await delay(75);
    document.title = `${baseTitle} | ==>`;
    await delay(100);
    document.title = `${baseTitle} | ===>`;
    await delay(75);
    document.title = `${baseTitle} | =====>`;
    await delay(175);
    document.title = `${baseTitle} | =======>`;
    await delay(75);
    document.title = `${baseTitle}${pageTitle ? " | " : ""}${pageTitle}`;
  } else if (!isNullOrWhiteSpace(pageTitle)) {
    document.title = pageTitle.toString();
  }
};

/**
 * Walks from an element up to the document and returns the outermost explicit `z-index` found, which is the
 * value a teleported element has to clear to render above the stacking context its anchor lives in.
 * @param $elem The element to walk up from.
 * @returns The outermost non-`auto` `z-index` as a string, or an empty string when no ancestor sets one.
 */
export const getZIndexRelativeToBody = ($elem: HTMLElement | null): string => {
  let $el: HTMLElement | Document | ParentNode | null = $elem;
  let zIndexVal = "";
  for (; $el && $el !== document; $el = $el.parentNode) {
    const val =
      document.defaultView
        ?.getComputedStyle($el as unknown as Element, null)
        .getPropertyValue("z-index")
        ?.trim() ?? "";
    if (val !== "" && val !== "auto") {
      zIndexVal = val;
    }
  }

  return zIndexVal;
};

/**
 * Modal and backdrop `z-index` pair that clears everything currently rendered on the page.
 * @returns The `z-index` to render a modal and its backdrop at.
 */
export const determineZIndexForModal = (): IZIndex => {
  const defaultModalZIndex = 1050;

  const maxZIndex = Math.max(
    ...Array.from(document.querySelectorAll("body *"), (el) => {
      if (el.closest('[class*="Toastify"]')) {
        return NaN;
      }
      const style = window.getComputedStyle(el);
      if (style.visibility === "hidden" || style.display === "none") {
        return NaN;
      }
      return parseFloat(style.zIndex);
    }).filter((zIndex) => !Number.isNaN(zIndex) && zIndex < Z_INDEX_ABUSE_THRESHOLD),
    0
  );

  const zIdx = maxZIndex + 5 > defaultModalZIndex ? maxZIndex + 5 : defaultModalZIndex;
  return {
    modalZ: zIdx + 1,
    backdropZ: zIdx,
  };
};

/**
 * Highest explicit `z-index` among the elements matching a selector. A teleported overlay reads this to
 * stack above whatever is already open, since walking up from its own root only reaches `body`.
 * @param selector Selector for the stacking contexts to clear.
 * @returns The highest numeric `z-index` found, or 0 when none of the matches set one.
 */
export const getHighestZIndex = (selector: string): number => {
  let highest = 0;
  document.querySelectorAll<HTMLElement>(selector).forEach(($el) => {
    const zIndex = parseFloat(document.defaultView?.getComputedStyle($el).zIndex ?? "");
    if (!Number.isNaN(zIndex)) {
      highest = Math.max(highest, zIndex);
    }
  });

  return highest;
};

const positionToCompassPointRelationship = (
  position: "top" | "right" | "left" | "bottom" | "auto"
): { parent: CompassPoint; child: CompassPoint } => {
  switch (position) {
    case "top":
      return { parent: "N", child: "S" };
    case "bottom":
      return { parent: "S", child: "N" };
    case "left":
      return { parent: "W", child: "E" };
    case "right":
      return { parent: "E", child: "W" };
    default:
      return { parent: "N", child: "S" };
  }
};

const calculatePosition = (
  $parentRect: DOMRect,
  $popViewPortRect: DOMRect,
  position: "top" | "right" | "left" | "bottom" | "auto" = "auto"
): "top" | "right" | "bottom" | "left" => {
  if (position !== "auto") {
    return position;
  }

  //this might not be 100% right:
  const leftSpace = $parentRect.left - $popViewPortRect.width;
  const topSpace = $parentRect.top - $popViewPortRect.height;
  const rightSpace = window.innerWidth - $parentRect.right + $popViewPortRect.width;
  const bottomSpace = window.innerHeight - $parentRect.bottom + $popViewPortRect.height;

  const maxSpace = Math.max(leftSpace, topSpace, rightSpace, bottomSpace);
  if (maxSpace === topSpace) {
    return "top";
  } else if (maxSpace === leftSpace) {
    return "left";
  } else if (maxSpace === rightSpace) {
    return "right";
  } else {
    return "bottom";
  }
};

const getAnchorPointXy = (
  box: DOMRect,
  compassPoint: CompassPoint,
  windowXOffset: number,
  windowYOffset: number
): { x: number; y: number } => {
  switch (compassPoint) {
    case "N":
      return { y: box.y + windowYOffset, x: box.x + box.width / 2 + windowXOffset };
    case "S":
      return { y: box.bottom + windowYOffset, x: box.x + box.width / 2 + windowXOffset };
    case "E":
      return { y: box.y + box.height / 2 + windowYOffset, x: box.x + box.width + windowXOffset };
    case "W":
      return { y: box.y + box.height / 2 + windowYOffset, x: box.x + windowXOffset };
    case "NW":
      return { y: box.y + windowYOffset, x: box.x + windowXOffset };
    case "NE":
      return { y: box.y + windowYOffset, x: box.x + box.width + windowXOffset };
    case "SW":
      return { y: box.bottom + windowYOffset, x: box.x + windowXOffset };
    case "SE":
      return { y: box.bottom + windowYOffset, x: box.x + box.width + windowXOffset };
  }
};

/**
 * Calculates the absolute `top`/`left`/`zIndex` CSS values needed to position a teleported (portalled) element
 * relative to an anchor element, accounting for page scroll and z-index stacking.
 * @param $anchorElement The element to position relative to.
 * @param $teleportedElement The teleported element whose dimensions are used for alignment.
 * @param position The desired placement. Can be a cardinal-point relationship object or a shorthand
 *   string (`"top"`, `"right"`, `"bottom"`, `"left"`, or `"auto"` to pick the side with the most space).
 * @param isCustomerWeb When true, forces `zIndex` to `"5000"` for customer-facing pages. Defaults to false.
 * @param offset Additional pixel offset applied to the final position. Defaults to `{ x: 0, y: 0 }`.
 * @returns An `IDomPosition` object with `top`, `left`, `zIndex`, and optionally `autoSelectedPosition`.
 */
export const recalculateTeleportedElementPosition = (
  $anchorElement: HTMLElement,
  $teleportedElement: HTMLElement,
  position: { parent: CompassPoint; child: CompassPoint } | "top" | "right" | "left" | "bottom" | "auto",
  isCustomerWeb: boolean = false,
  offset: { x: number; y: number } = { x: 0, y: 0 }
): IDomPosition => {
  const anchorBox = $anchorElement?.getBoundingClientRect();
  const linkedBox = $teleportedElement?.getBoundingClientRect();
  const zIndex = getZIndexRelativeToBody($anchorElement);
  const rtn: IDomPosition = { top: "", left: "", zIndex: "" };
  if (anchorBox && linkedBox) {
    const windowXOffset = window.pageXOffset;
    const windowYOffset = window.pageYOffset;
    if (zIndex && isInt(zIndex) && parseInt(zIndex) > -99999) {
      rtn.zIndex = (parseInt(zIndex) + 10).toString();
    }

    let compassRelation: { parent: CompassPoint; child: CompassPoint };

    if (typeof position === "string") {
      const autoPosition = calculatePosition(anchorBox, linkedBox, position);
      rtn.autoSelectedPosition = autoPosition;
      compassRelation = positionToCompassPointRelationship(autoPosition);
    } else {
      compassRelation = position;
    }
    const parentPoint = getAnchorPointXy(anchorBox, compassRelation.parent, windowXOffset, windowYOffset);
    switch (compassRelation.child) {
      case "N":
        rtn.top = `${parentPoint.y + offset.y}px`;
        rtn.left = `${parentPoint.x + offset.x - linkedBox.width / 2}px`;
        break;
      case "S":
        rtn.top = `${parentPoint.y + offset.y - linkedBox.height}px`;
        rtn.left = `${parentPoint.x + offset.x - linkedBox.width / 2}px`;
        break;
      case "E":
        rtn.top = `${parentPoint.y + offset.y - linkedBox.height / 2}px`;
        rtn.left = `${parentPoint.x + offset.x - linkedBox.width}px`;
        break;
      case "W":
        rtn.top = `${parentPoint.y + offset.y - linkedBox.height / 2}px`;
        rtn.left = `${parentPoint.x + offset.x}px`;
        break;
      case "NW":
        rtn.top = `${parentPoint.y + offset.y}px`;
        rtn.left = `${parentPoint.x + offset.x}px`;
        break;
      case "NE":
        rtn.top = `${parentPoint.y + offset.y}px`;
        rtn.left = `${parentPoint.x + offset.x - linkedBox.width}px`;
        break;
      case "SW":
        rtn.top = `${parentPoint.y + offset.y - linkedBox.height}px`;
        rtn.left = `${parentPoint.x + offset.x + linkedBox.width}px`;
        break;
      case "SE":
        rtn.top = `${parentPoint.y + offset.y - linkedBox.height}px`;
        rtn.left = `${parentPoint.x + offset.x - linkedBox.width}px`;
        break;
    }
  }

  if (isCustomerWeb) {
    const dynamicZIndex = rtn.zIndex ? parseInt(rtn.zIndex) : 0;
    rtn.zIndex = Math.max(dynamicZIndex, 5000).toString();
  }

  return rtn;
};

const TEMPLATE_SAMPLE_PAGE_HEAD_SELECTOR = [
  "title",
  'link[rel="canonical"]',
  'meta[property="og:url"]',
  'meta[property="og:title"]',
  'meta[property="article:modified_time"]',
].join(", ");

const PAGE_OWNED_HEAD_SELECTORS = [
  'meta[name="robots"]',
  'meta[name="description"]',
  'meta[property="og:description"]',
  'meta[property="og:type"]',
  'meta[property="og:site_name"]',
];

/**
 * Whether a head tag from the brand template describes a page rather than the brand. The template is scraped
 * from a sample page and rendered for the template api call, so its title, canonical and og:url name those;
 * its robots and description tags apply only when the server rendered none of its own for this page.
 * @param element A head element from the parsed template markup.
 * @returns `true` when the element must not be copied into the live document head.
 */
export const isPageSpecificTemplateHeadTag = (element: Element): boolean =>
  element.matches(TEMPLATE_SAMPLE_PAGE_HEAD_SELECTOR) ||
  PAGE_OWNED_HEAD_SELECTORS.some(
    (selector) => element.matches(selector) && document.head.querySelector(selector) !== null
  );

const captureLateDomContentLoadedListeners = (): (() => void) => {
  if (document.readyState === "loading") {
    return () => {};
  }

  const listeners: EventListenerOrEventListenerObject[] = [];
  const nativeAddEventListener = document.addEventListener;
  document.addEventListener = ((
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions
  ) => {
    if (type === "DOMContentLoaded" && listener) {
      listeners.push(listener);
      return;
    }
    nativeAddEventListener.call(document, type, listener, options);
  }) as typeof document.addEventListener;

  return () => {
    document.addEventListener = nativeAddEventListener;
    const event = new Event("DOMContentLoaded");
    listeners.forEach((listener) =>
      typeof listener === "function" ? listener.call(document, event) : listener.handleEvent(event)
    );
  };
};

/**
 * Parses and injects a page template's markup (head elements, stylesheets, and scripts) into the current document,
 * then moves the `#s1` app root into the template's designated content slot. Scripts are executed in order after
 * all stylesheets have loaded. A `<meta name="s1-template-applied">` guard prevents double-application.
 * @param templateObj The template markup object containing the HTML string and brand layout metadata.
 * @param preloadTransformation An optional callback invoked with the parsed template `Document` before any
 *   elements are moved, allowing transformations prior to injection.
 * @returns A promise that resolves to `true` if the template was applied, or `false` if it was skipped
 *   (already applied or markup was empty).
 */
export const applyTemplateMarkup = async (
  templateObj?: ITemplateMarkup,
  preloadTransformation?: (template: Document) => void
): Promise<boolean> => {
  if (isNullOrWhiteSpace(templateObj?.TemplateMarkup)) {
    return false;
  }

  if (document.head.querySelector('meta[name="s1-template-applied"]')) {
    return false;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(templateObj!.TemplateMarkup, "text/html");
  if (preloadTransformation) {
    preloadTransformation(doc);
  }

  //remove the S1 backend bootstrap'ed scripts cause that has legacy stuff we don't need or want
  [...document.querySelectorAll('[data-s1-loaded="1"]')].forEach((elem) => elem.remove());

  // Drop the backend-rendered asset SCRIPTS from the template before collecting the rest. The
  // template comes from ContentApi/get-template-markup, which renders the same master — so it
  // carries its own copy of the {{{JsBundle}}} tags. This code is running *from* that bundle, so
  // re-appending them fetches and executes it a second time (and `defer` is ignored on
  // dynamically-inserted scripts, so the runtime/entry pair would race and could end up with two
  // webpack runtimes sharing one chunk-loading global). Stylesheet links are deliberately left
  // alone: the live document's copies were just removed, and the template's copies replace them.
  doc.querySelectorAll('script[data-s1-loaded="1"]').forEach((elem) => elem.remove());

  const headScripts: { $el: HTMLScriptElement; isRemote: boolean }[] = [];
  const bodyScripts: { $el: HTMLScriptElement; isRemote: boolean }[] = [];
  doc.head.querySelectorAll("script").forEach((scriptElem) => {
    headScripts.push({
      $el: scriptElem as HTMLScriptElement,
      isRemote: !isNullOrWhiteSpace(scriptElem.getAttribute("src")),
    });
    scriptElem.remove();
  });

  doc.body.querySelectorAll("script").forEach((scriptElem) => {
    bodyScripts.push({
      $el: scriptElem as HTMLScriptElement,
      isRemote: !isNullOrWhiteSpace(scriptElem.getAttribute("src")),
    });
    scriptElem.remove();
  });

  const headElements = Array.from(doc.head.children);
  const bodyElements = Array.from(doc.body.children);
  const cssLoaded: string[] = [];
  let cssOnLoadedCalled = false;
  const onAllCssFilesLoaded = async (): Promise<boolean> => {
    if (cssOnLoadedCalled) {
      return false;
    }
    cssOnLoadedCalled = true;

    const replayDomContentLoaded = captureLateDomContentLoadedListeners();

    // Document order, as the browser would run them: a library's inline config precedes the library.
    for (const { $el } of headScripts) {
      await appendAndExecuteScript(document.head, $el);
    }

    await delay(5);

    // WordPress themes key layout off body classes, e.g. Divi's et_no_sidebar hides its sidebar divider.
    document.body.classList.add(...Array.from(doc.body.classList));

    let foundContentElement = false;
    const contentCssSelector = templateObj!.BrandLayout.PublicContentSelectorCssClass;
    const s1Node = document.querySelector("#s1") ?? document.querySelector("#root");
    bodyElements.forEach((element) => {
      let contentElement: HTMLElement | null = element.classList.contains(contentCssSelector)
        ? (element as HTMLElement)
        : null;

      if (!contentElement && element.querySelector(`.${contentCssSelector}`)) {
        // Divi nests the header and footer in the same wrapper, so emptying it takes the chrome too.
        contentElement = element.querySelector("header, footer")
          ? element.querySelector<HTMLElement>(`.${contentCssSelector}`)
          : (element as HTMLElement);
      }

      if (contentElement) {
        foundContentElement = true;
        document.body.appendChild(element);
        contentElement.innerHTML = "";
        contentElement.appendChild(s1Node!);
      } else if (!foundContentElement) {
        document.body.insertBefore(element, null);
      } else {
        document.body.appendChild(element);
      }
    });
    const s1Meta = document.createElement("meta");
    s1Meta.setAttribute("name", "s1-template-applied");
    s1Meta.setAttribute("content", "true");
    document.head.appendChild(s1Meta);

    for (const { $el } of bodyScripts) {
      await appendAndExecuteScript(document.body, $el);
    }
    replayDomContentLoaded();

    return true;
  };

  const cssFileOnLoaded = async (cssFile: string) => {
    cssLoaded.push(cssFile);
    if (cssLoaded.length === cssFiles.length) {
      await delay(3);
      return onAllCssFilesLoaded();
    }
  };
  const cssFiles = headElements.filter(
    (x) => x.tagName === "LINK" && x.getAttribute("rel") === "stylesheet" && x.getAttribute("href")
  );
  cssFiles.forEach((linkElement) => {
    (linkElement as HTMLLinkElement).onload = () => cssFileOnLoaded(linkElement.getAttribute("href")!);
    (linkElement as HTMLLinkElement).onerror = () => cssFileOnLoaded(linkElement.getAttribute("href")!);
  });
  headElements
    .filter((x) => !isPageSpecificTemplateHeadTag(x))
    .forEach((element) => {
      document.head.appendChild(element);
    });

  if (cssFiles.length === 0) {
    return onAllCssFilesLoaded();
  }
  //fallback in case onload/onerror don't fire
  await delay(3000);
  onAllCssFilesLoaded();
  return true;
};

/**
 * Appends a script element to a destination element and waits for it to finish loading before resolving.
 * Inline scripts, deferred scripts, and async scripts are appended immediately without waiting.
 * Remote synchronous scripts are polled until their `onload`/`onerror` fires or a 10-second timeout elapses.
 * @param destination The element (`head` or `body`) to append the script to.
 * @param scriptElem The source `<script>` element whose attributes and content are cloned and executed.
 * @returns A promise that resolves to `true` if the script loaded successfully, or `false` on timeout.
 */
export const appendAndExecuteScript = async (
  destination: HTMLElement,
  scriptElem: HTMLScriptElement
): Promise<boolean> => {
  const scriptKey = scriptElem.src || scriptElem.innerHTML;
  const newScript = document.createElement("script");
  Array.from(scriptElem.attributes).forEach((attr) => {
    newScript.setAttribute(attr.name, attr.value);
  });
  newScript.appendChild(document.createTextNode(scriptElem.innerHTML));
  if (
    isNullOrWhiteSpace(scriptElem.src) ||
    scriptElem.attributes.getNamedItem("defer") ||
    scriptElem.attributes.getNamedItem("async")
  ) {
    destination.appendChild(newScript);
    return true;
  }

  const scriptKeysLoaded: string[] = [];
  newScript.onload = () => {
    scriptKeysLoaded.push(scriptKey);
  };
  newScript.onerror = () => {
    console.warn("Error loading script while applying to template", scriptKey);
    scriptKeysLoaded.push(scriptKey);
  };

  destination.appendChild(newScript);
  let attempts = 0;
  while (scriptKeysLoaded.indexOf(scriptKey) === -1) {
    await delay(1);
    if (scriptKeysLoaded.indexOf(scriptKey) !== -1) {
      break;
    }
    attempts++;
    if (attempts >= 10000) {
      console.warn("Script load timeout while applying to template", scriptKey);
      break;
    }
  }

  return scriptKeysLoaded.includes(scriptKey);
};
